import { DropdownWithLabel } from '@blueprint/dropdown';
import { MultiColumnDropdownWithLabel } from '@blueprint/multicolumn-dropdown';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { buildSelectPriceTitleToRent } from '@zoopla/redux-reducer-filters';
import { DropdownContainer } from '../DropdownContainer';

const minPriceLabel = 'Minimum price';
const maxPriceLabel = 'Maximum price';

const AllDropdownsWrapper = styled.div(
    () => css`
        min-width: 28.525rem;
    `
);

const StyledDropdownToRentPrice = styled(DropdownWithLabel)(
    () => css`
        min-width: 15rem;
    `
);

const DropdownWrapper = styled.div(
    () => css`
        display: flex;
    `
);

const priceFrequencyOptions: { [key: PriceFrequency]: string } = {
    per_month: 'pcm',
    per_week: 'pcw'
};

const priceOptions = [
    '25',
    '50',
    '75',
    '100',
    '125',
    '150',
    '175',
    '200',
    '225',
    '250',
    '275',
    '300',
    '325',
    '350',
    '375',
    '400',
    '425',
    '450',
    '475',
    '500',
    '550',
    '600',
    '650',
    '700',
    '750',
    '800',
    '850',
    '900',
    '950',
    '1000',
    '1250',
    '1500',
    '1750',
    '2000',
    '2500',
    '3000',
    '3500',
    '4000',
    '4500',
    '5000',
    '7500'
];

type prices = typeof priceOptions[number];

export type PriceFrequency = '' | 'per_week' | 'per_month' | string;
export type PriceMin = prices;
export type PriceMax = prices;

type SelectPriceToRentProps = {
    value: {
        priceMin: PriceMin;
        priceMax: PriceMax;
        priceFrequency: PriceFrequency;
    };
    onChange: ({
        priceMin,
        priceMax,
        priceFrequency
    }: {
        priceMin: PriceMin;
        priceMax: PriceMax;
        priceFrequency: PriceFrequency;
    }) => void;
};

export const SelectPriceToRent = ({
    value,
    onChange
}: SelectPriceToRentProps) => {
    const priceButtonTextToRent = buildSelectPriceTitleToRent(
        value.priceMin,
        value.priceMax,
        value.priceMin,
        value.priceMax,
        value.priceFrequency
    );

    const priceMinOptions =
        value.priceMax !== ''
            ? priceOptions.slice(0, priceOptions.indexOf(value.priceMax) + 1)
            : priceOptions;

    const priceMaxOptions =
        value.priceMin !== ''
            ? priceOptions.slice(priceOptions.indexOf(value.priceMin))
            : priceOptions;

    const priceFrequency = priceFrequencyOptions[value.priceFrequency];

    return (
        <MultiColumnDropdownWithLabel
            title={priceButtonTextToRent}
            label="Price range"
            testId="any_price"
            isLeft
        >
            <AllDropdownsWrapper>
                <DropdownContainer>
                    <DropdownWithLabel
                        id="desktop_price_frequency-filter"
                        data-testid="desktop_price_frequency-filter-select"
                        onChange={(e) => {
                            onChange({
                                ...value,
                                priceFrequency: e.target.value
                            });
                        }}
                        value={value.priceFrequency}
                        name="priceFrequency"
                        hasBorderRadius
                        aria-label="Price Frequency"
                        label="Price per"
                    >
                        <option value="per_month">Month</option>
                        <option value="per_week">Week</option>
                    </DropdownWithLabel>
                </DropdownContainer>

                <DropdownWrapper>
                    <DropdownContainer>
                        <StyledDropdownToRentPrice
                            onChange={(e) => {
                                onChange({
                                    ...value,
                                    priceMin: e.target.value
                                });
                            }}
                            value={value.priceMin}
                            name="price_min"
                            id="price_min"
                            hasBorderRadius
                            aria-label="Min Price"
                            data-testid="min_price"
                            label={minPriceLabel}
                        >
                            <option value="">No min</option>
                            {priceMinOptions.map((priceMinOption, index) => {
                                const price = new Intl.NumberFormat('en-GB', {
                                    style: 'currency',
                                    currency: 'GBP',
                                    maximumFractionDigits: 0,
                                    minimumFractionDigits: 0
                                }).format(Number(priceMinOption));

                                return (
                                    <option
                                        value={priceMinOption}
                                        key={`quicksearch_price_min_${index}`}
                                    >
                                        {price} {priceFrequency}
                                    </option>
                                );
                            })}
                        </StyledDropdownToRentPrice>
                    </DropdownContainer>
                    <DropdownContainer>
                        <StyledDropdownToRentPrice
                            onChange={(e) => {
                                onChange({
                                    ...value,
                                    priceMax: e.target.value
                                });
                            }}
                            value={value.priceMax}
                            name="price_max"
                            id="price_max"
                            hasBorderRadius
                            aria-label="Max Price"
                            data-testid="max_price"
                            label={maxPriceLabel}
                        >
                            <option value="">No max</option>
                            {priceMaxOptions.map((priceMaxOption, index) => {
                                const price = new Intl.NumberFormat('en-GB', {
                                    style: 'currency',
                                    currency: 'GBP',
                                    maximumFractionDigits: 0,
                                    minimumFractionDigits: 0
                                }).format(Number(priceMaxOption));

                                return (
                                    <option
                                        value={priceMaxOption}
                                        key={`price_max_${index}`}
                                    >
                                        {price} {priceFrequency}
                                    </option>
                                );
                            })}
                        </StyledDropdownToRentPrice>
                    </DropdownContainer>
                </DropdownWrapper>
            </AllDropdownsWrapper>
        </MultiColumnDropdownWithLabel>
    );
};
